/**
 * Permission-key catalog (spec §1 / §3.2).
 *
 * These are the feature-area keys the backend returns in
 * GET /user/me/permissions. The admin consoles (Phases 2–3) must render their
 * checkbox lists from the live API (`/platform/permissions`,
 * `/company-admin/permissions`) — do NOT hardcode those. This file only exists so
 * the static nav/route gating in Phase 1 references keys by constant instead of
 * by loose string literal.
 */
export const PERMISSIONS = {
  ASK: 'ask',
  TRANSLATE: 'translate',
  KB_DIM1: 'kb_dim1',
  KB_DIM3: 'kb_dim3',
  AGENTS: 'agents',
  COMPOSIO: 'composio',
  CUSTOM_TOOLS: 'custom_tools',
  TEAMS: 'teams',
  TEMPLATES: 'templates',
  WEBHOOKS: 'webhooks',
  SCHEDULES: 'schedules',
  TRIGGERS: 'triggers',
  BATCH_JOBS: 'batch_jobs',
  DASHBOARD: 'dashboard',
  LLM_KEYS_USER: 'llm_keys_user',
  LLM_KEYS_COMPANY: 'llm_keys_company',
  USER_MANAGEMENT: 'user_management',
  ROLE_MANAGEMENT: 'role_management',
  RESOURCE_SHARING: 'resource_sharing',
  USAGE_ANALYTICS: 'usage_analytics',
  MCP_SERVERS: 'mcp_servers',
  // Use Quadroid from AI apps (Claude, ChatGPT, Cursor…) with a personal
  // connection key. Off everywhere by default; partners CAN pass it on.
  PLATFORM_MCP: 'platform_mcp',
  // Faceted KG datasets (`/knowledge-graph`). Deliberately NOT in the default
  // member role, so the whole area stays hidden unless the key is granted.
  KNOWLEDGE_GRAPH: 'knowledge_graph',
  // Platform-scope keys (gate sections of the Platform Admin console)
  PLATFORM_COMPANIES: 'platform_companies',
  PLATFORM_USERS: 'platform_users',
  PLATFORM_ROLES: 'platform_roles',
  PLATFORM_LLM_CATALOG: 'platform_llm_catalog',
  PLATFORM_AUDIT: 'platform_audit',
  PLATFORM_USAGE_ANALYTICS: 'platform_usage_analytics',
  PLATFORM_PARTNERS: 'platform_partners',
  // Company-scope opt-in key (reading other users' prompts/answers). Platform-only
  // decision — a partner can never grant it to its companies.
  USAGE_RUN_CONTENT: 'usage_run_content',
};

/**
 * Partner-scope keys (Partner tier guide §3.3). These come ONLY from the
 * `partner_admin` role and arrive in `partner_permissions` of
 * /user/me/permissions — never put them in a custom role (backend 400s).
 */
export const PARTNER_PERMISSIONS = {
  COMPANIES: 'partner_companies',
  USERS: 'partner_users',
  ENTITLEMENTS: 'partner_entitlements',
  LLM_KEYS: 'partner_llm_keys',
  SHARING: 'partner_sharing',
  USAGE_ANALYTICS: 'partner_usage_analytics',
  USAGE_RUN_CONTENT: 'partner_usage_run_content',
  AUDIT: 'partner_audit',
};

/**
 * Labels for the partner keys. The partner area can't call /platform/permissions
 * (the live catalog), so it needs these shipped with the frontend.
 */
export const PARTNER_PERMISSION_LABELS = {
  [PARTNER_PERMISSIONS.COMPANIES]: 'Partner Companies',
  [PARTNER_PERMISSIONS.USERS]: 'Partner Users',
  [PARTNER_PERMISSIONS.ENTITLEMENTS]: 'Partner Feature Limits',
  [PARTNER_PERMISSIONS.LLM_KEYS]: 'Partner LLM Keys',
  [PARTNER_PERMISSIONS.SHARING]: 'Partner Sharing',
  [PARTNER_PERMISSIONS.USAGE_ANALYTICS]: 'Partner Usage Analytics',
  [PARTNER_PERMISSIONS.USAGE_RUN_CONTENT]: 'Partner Run Content',
  [PARTNER_PERMISSIONS.AUDIT]: 'Partner Audit Logs',
};

/**
 * Labels for company keys, used only where the live catalog may not list a key
 * (the partner company-limits editor falls back to these before humanizing).
 */
export const PERMISSION_LABEL_FALLBACKS = {
  [PERMISSIONS.PLATFORM_MCP]: 'Platform MCP',
};

/** System role names the UI special-cases (badges, pickers, read-only rules). */
export const ROLE_NAMES = {
  PLATFORM_ADMIN: 'platform_admin',
  PLATFORM_STAFF: 'platform_staff',
  PARTNER_ADMIN: 'partner_admin',
  COMPANY_ADMIN: 'company_admin',
  COMPANY_MEMBER: 'company_member',
  SCHEDULE_USER: 'schedule_user',
};

/** Names the backend refuses for custom roles. */
export const RESERVED_ROLE_NAMES = Object.values(ROLE_NAMES);

export default PERMISSIONS;
